/**
 * Sistema di gestione versione per l'app Chakur
 * Permette l'aggiornamento automatico dei dati quando la versione dell'app cambia
 */

// Chiave utilizzata per memorizzare la versione nei dati locali
const VERSION_KEY = "chakur_app_version";
const BUILD_INFO_KEY = "chakur_build_info";
const CACHE_BUSTER = "?v=" + Date.now(); // Forza il bypass della cache
const BUILD_INFO_URL = "/build-info.json" + CACHE_BUSTER;

// Versione corrente dell'app (viene sovrascritta al caricamento)
let APP_VERSION = "1.1.7";

// Flag per forzare l'aggiornamento delle risorse
let FORCE_REFRESH = false;

export { APP_VERSION, FORCE_REFRESH };
/**
 * Verifica se è necessario un aggiornamento dei dati
 * @returns {Promise<{needsUpdate: boolean, oldVersion: string|null, currentVersion: string, isNewBuild: boolean}>}
 */
export async function checkVersionUpdate() {
  console.log('Verifica aggiornamenti in corso...');
  let buildInfo;

  try {
    // Forza il bypass della cache per il file di build info
    const response = await fetch(BUILD_INFO_URL, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      throw new Error('Impossibile caricare le informazioni di build');
    }

    buildInfo = await response.json();
    console.log('Build info dal server:', buildInfo);

    // Aggiorna la versione corrente con quella del server
    APP_VERSION = buildInfo.version;

    // Verifica se è un nuovo build
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const isNewBuild = storedVersion !== buildInfo.version;

    // Se è un nuovo build o se dobbiamo forzare l'aggiornamento
    if (isNewBuild || FORCE_REFRESH) {
      console.log('Nuova versione rilevata o aggiornamento forzato');

      // Aggiorna la versione memorizzata
      localStorage.setItem(VERSION_KEY, buildInfo.version);
      localStorage.setItem(BUILD_INFO_KEY, JSON.stringify(buildInfo));

      // Se è un nuovo build, NON forzare il ricaricamento automatico per evitare loop su iOS
      // Lasciamo che sia l'utente a cliccare "Aggiorna" nell'alert
      if (isNewBuild) {
        console.log('Nuovo build rilevato, versione aggiornata in localStorage');
      }

      return {
        needsUpdate: true,
        oldVersion: storedVersion,
        currentVersion: buildInfo.version,
        isNewBuild
      };
    }

    return {
      needsUpdate: false,
      oldVersion: storedVersion,
      currentVersion: buildInfo.version,
      isNewBuild: false
    };
  } catch (error) {
    console.error('Errore durante il controllo degli aggiornamenti:', error);

    // In caso di errore, usa i dati memorizzati localmente
    const storedInfo = localStorage.getItem(BUILD_INFO_KEY);
    const buildInfo = storedInfo ? JSON.parse(storedInfo) : { version: APP_VERSION };

    return {
      needsUpdate: false,
      oldVersion: buildInfo.version,
      currentVersion: buildInfo.version,
      isNewBuild: false,
      error: error.message
    };
  }
}

/**
 * Aggiorna la versione memorizzata dopo aver completato l'aggiornamento
 */
export function updateStoredVersion() {
  localStorage.setItem(VERSION_KEY, APP_VERSION);
}

/**
 * Resetta dati specifici in base alla versione
 * @param {string} oldVersion - Versione precedente (può essere null se prima installazione)
 * @param {string} newVersion - Nuova versione
 */
export function performVersionBasedUpdates(oldVersion, newVersion) {
  console.log(`Aggiornamento da versione ${oldVersion || 'iniziale'} a ${newVersion}`);

  // Aggiorna solo i dati necessari in base alle versioni specifiche
  // Questo permette di mantenere i dati dell'utente quando possibile

  if (!oldVersion) {
    // Prima installazione, inizializza tutto
    resetAllData();
    return;
  }

  // Esempi di aggiornamenti selettivi (da personalizzare per ogni versione)
  if (oldVersion === "1.0.0" && newVersion === "1.1.0") {
    // Aggiornamento specifico per passare da 1.0.0 a 1.1.0
    // localStorage.removeItem("some_outdated_key");
  }

  // Gestione delle incompatibilità principali (major versions)
  if (oldVersion.startsWith("1.") && newVersion.startsWith("2.")) {
    // Aggiornamento tra major versions potrebbe richiedere reset completo
    resetAllData();
  }
}

/**
 * Reset completo di tutti i dati dell'app
 * Da usare solo quando strettamente necessario
 */
export function resetAllData() {
  console.log("Avvio reset completo dei dati dell'app...");

  // Lista delle chiavi da non cancellare (preferenze utente, ecc.)
  const keysToKeep = [VERSION_KEY, "user_preferences"];

  // Salva temporaneamente i valori da mantenere
  const savedValues = {};
  keysToKeep.forEach(key => {
    savedValues[key] = localStorage.getItem(key);
  });

  // Cancella tutto localStorage
  localStorage.clear();

  // Imposta la versione corrente dell'app
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  console.log(`Versione dell'app impostata a: ${APP_VERSION}`);

  // Ripristina i valori da mantenere (eccetto VERSION_KEY che abbiamo già impostato)
  Object.keys(savedValues).forEach(key => {
    if (key !== VERSION_KEY && savedValues[key] !== null) {
      localStorage.setItem(key, savedValues[key]);
    }
  });

  console.log("Reset completo dei dati dell'app completato");
}

/**
 * Mostra messaggio di aggiornamento all'utente
 * @param {Object} versionInfo - Informazioni sull'aggiornamento
 * @returns {string} Messaggio da mostrare
 */
export function getUpdateMessage(versionInfo) {
  if (versionInfo.oldVersion) {
    return `L'app è stata aggiornata dalla versione ${versionInfo.oldVersion} alla ${versionInfo.currentVersion}. Alcuni dati potrebbero essere stati aggiornati automaticamente.`;
  } else {
    return `Benvenuto nella versione ${versionInfo.currentVersion} dell'app!`;
  }
}
